import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import type { AuditException } from '@/types/asset-audit';
import { Mail } from 'lucide-react';

interface ContactAuditorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  exception: AuditException | null;
  onSend: (data: ContactAuditorData) => void;
}

export interface ContactAuditorData {
  to: string;
  subject: string;
  message: string;
  includeExceptionDetails: boolean;
  ccMe: boolean;
}

export function ContactAuditorModal({
  open,
  onOpenChange,
  exception,
  onSend,
}: Readonly<ContactAuditorModalProps>) {
  const [formData, setFormData] = useState<ContactAuditorData>({
    to: exception?.reportedByName ?? '',
    subject: `Re: Exception ${exception?.id} - ${exception?.exceptionType}`,
    message: '',
    includeExceptionDetails: true,
    ccMe: true,
  });

  React.useEffect(() => {
    if (exception) {
      setFormData({
        to: exception.reportedByName ?? '',
        subject: `Re: Exception ${exception.id} - ${exception.exceptionType}`,
        message: '',
        includeExceptionDetails: true,
        ccMe: true,
      });
    }
  }, [exception]);

  const handleSend = () => {
    onSend(formData);
    onOpenChange(false);
  };

  if (!exception) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Contact Auditor
          </DialogTitle>
          <DialogDescription>
            Send a message to the auditor who reported this exception.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Exception Info */}
          <div className="rounded-lg bg-gray-50 p-3 text-sm">
            <div className="font-medium mb-1">Exception Details</div>
            <div className="text-gray-600">
              <div>Asset: {exception.assetCode} - {exception.assetName}</div>
              <div>Type: {exception.exceptionType}</div>
              <div>Severity: {exception.severity}</div>
            </div>
          </div>

          {/* To Field */}
          <div className="space-y-2">
            <Label htmlFor="to">To</Label>
            <Input
              id="to"
              value={formData.to}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, to: e.target.value })}
              placeholder="Auditor email"
            />
          </div>

          {/* Subject Field */}
          <div className="space-y-2">
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              value={formData.subject}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, subject: e.target.value })}
              placeholder="Email subject"
            />
          </div>

          {/* Message Field */}
          <div className="space-y-2">
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, message: e.target.value })}
              placeholder="Type your message here..."
              rows={6}
              className="resize-none"
            />
          </div>

          {/* Checkboxes */}
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="includeDetails"
                checked={formData.includeExceptionDetails}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, includeExceptionDetails: checked as boolean })
                }
              />
              <Label
                htmlFor="includeDetails"
                className="text-sm font-normal cursor-pointer"
              >
                Include exception details in email
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="ccMe"
                checked={formData.ccMe}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, ccMe: checked as boolean })
                }
              />
              <Label
                htmlFor="ccMe"
                className="text-sm font-normal cursor-pointer"
              >
                CC me on response
              </Label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={!formData.message.trim()}>
            <Mail className="h-4 w-4 mr-2" />
            Send Message
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
